import { useEffect, useState } from "react";
import { adminApi, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Mail, Send, Loader2, Trash2, AlertTriangle, Users2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function EmailsManager() {
  const { user } = useAuth();
  const [subs, setSubs] = useState(null);
  const [testTo, setTestTo] = useState(user?.email || "");
  const [testing, setTesting] = useState(false);

  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [preheader, setPreheader] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [blasting, setBlasting] = useState(false);

  useEffect(() => {
    load();
  }, []);

  async function load() {
    try {
      const { data } = await adminApi.get("/admin/subscribers");
      setSubs(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  async function sendTest(e) {
    e.preventDefault();
    if (!testTo) return toast.error("Enter an email address first.");
    setTesting(true);
    try {
      const { data } = await adminApi.post("/admin/email/test", { to: testTo });
      toast.success(`Test email sent to ${data.to}. Check your inbox.`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setTesting(false);
    }
  }

  async function removeSub(sub) {
    if (!confirm(`Remove ${sub.email} from the list?`)) return;
    try {
      await adminApi.delete(`/admin/subscribers/${sub.id}`);
      setSubs((prev) => prev.filter((s) => s.id !== sub.id));
      toast.success("Removed.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  }

  async function sendBlast() {
    setBlasting(true);
    try {
      const { data } = await adminApi.post("/admin/email/blast", {
        subject: subject.trim(),
        body: body.trim(),
        preheader: preheader.trim() || null,
      });
      toast.success(
        `Blast sent — ${data.sent}/${data.total} delivered${
          data.failed ? ` · ${data.failed} failed` : ""
        }.`
      );
      setConfirmOpen(false);
      setSubject("");
      setBody("");
      setPreheader("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBlasting(false);
    }
  }

  const activeCount = subs?.filter((s) => s.active).length ?? 0;
  const canSend = subject.trim().length > 0 && body.trim().length > 0 && activeCount > 0;

  return (
    <div data-testid="emails-manager" className="space-y-10 max-w-3xl">
      <p className="text-sm text-[var(--trio-muted)] max-w-xl">
        Send a one-off test to make sure delivery is working, and blast styled
        emails to everyone on your subscriber list.
      </p>

      {/* --- Test send --- */}
      <section className="space-y-3">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--trio-primary)] font-bold">
            Send Test Email
          </div>
          <div className="text-xs text-[var(--trio-muted)] mt-1">
            While in Resend sandbox mode, only your signup email will actually deliver.
          </div>
        </div>
        <form onSubmit={sendTest} className="flex gap-2">
          <Input
            type="email"
            required
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="you@example.com"
            data-testid="email-test-to"
            className="rounded-none border-[var(--trio-border)] bg-[var(--trio-bg)]"
          />
          <button
            type="submit"
            disabled={testing}
            data-testid="email-test-send"
            className="inline-flex items-center gap-2 bg-[var(--trio-text)] hover:bg-[var(--trio-primary)] text-white px-6 py-2 text-xs tracking-widest uppercase font-bold disabled:opacity-60 transition-colors whitespace-nowrap"
          >
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
            {testing ? "Sending…" : "Send Test"}
          </button>
        </form>
      </section>

      {/* --- Blast composer --- */}
      <section className="space-y-4 border border-[var(--trio-border)] p-6 bg-[var(--trio-surface)]">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--trio-primary)] font-bold">
              Email Blast
            </div>
            <div className="text-xs text-[var(--trio-muted)] mt-1">
              Goes out to every active subscriber on your list.
            </div>
          </div>
          <div
            data-testid="blast-recipient-count"
            className="text-right shrink-0"
          >
            <div className="font-display text-4xl leading-none">{activeCount}</div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--trio-muted)] mt-1">
              Subscribers
            </div>
          </div>
        </div>

        <div>
          <Label className="text-xs tracking-widest uppercase">Subject</Label>
          <Input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Sunday Brunch is BACK 🍳"
            data-testid="blast-subject"
            maxLength={200}
            className="mt-2 rounded-none border-[var(--trio-border)] bg-[var(--trio-bg)]"
          />
        </div>

        <div>
          <Label className="text-xs tracking-widest uppercase">Preheader</Label>
          <div className="text-[11px] text-[var(--trio-muted)] mb-1">
            Optional preview line inside their inbox (shows next to the subject).
          </div>
          <Input
            value={preheader}
            onChange={(e) => setPreheader(e.target.value)}
            placeholder="Bottomless mimosas, catfish & grits, live music every Sunday."
            data-testid="blast-preheader"
            className="mt-1 rounded-none border-[var(--trio-border)] bg-[var(--trio-bg)]"
          />
        </div>

        <div>
          <Label className="text-xs tracking-widest uppercase">Message</Label>
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={8}
            placeholder={"Hey Trio family,\n\nStarting this Sunday we're bringing back brunch — 10am to 2pm every weekend. Come thru!"}
            data-testid="blast-body"
            className="mt-2 rounded-none border-[var(--trio-border)] bg-[var(--trio-bg)]"
          />
          <div className="text-[11px] text-[var(--trio-muted)] mt-1">
            Plain text with line breaks — we wrap it in the Trio email template automatically.
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 pt-2 border-t border-[var(--trio-border)]">
          <button
            type="button"
            disabled={!canSend}
            onClick={() => setConfirmOpen(true)}
            data-testid="blast-open-confirm"
            className="inline-flex items-center gap-2 bg-[var(--trio-primary)] hover:bg-[var(--trio-primary-hover)] text-white px-6 py-3 text-xs tracking-widest uppercase font-bold disabled:opacity-40 transition-colors"
          >
            <Send className="w-4 h-4" />
            Review & Send
          </button>
        </div>
      </section>

      {/* --- Subscribers table --- */}
      <section className="space-y-3">
        <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[var(--trio-primary)] font-bold">
          <Users2 className="w-4 h-4" />
          Subscribers · {subs?.length ?? "…"}
        </div>
        {subs === null ? (
          <div className="flex items-center gap-2 text-sm text-[var(--trio-muted)]">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : subs.length === 0 ? (
          <div className="border border-dashed border-[var(--trio-border)] p-8 text-center text-sm text-[var(--trio-muted)]">
            No one on the list yet. When guests sign up from the homepage, they'll show up here.
          </div>
        ) : (
          <div className="border border-[var(--trio-border)] divide-y divide-[var(--trio-border)]">
            {subs.map((s) => (
              <div
                key={s.id}
                data-testid={`subscriber-row-${s.id}`}
                className="flex items-center gap-3 px-4 py-3 text-sm bg-[var(--trio-bg)]"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{s.email}</div>
                  <div className="text-[11px] text-[var(--trio-muted)] uppercase tracking-widest">
                    {new Date(s.created_at).toLocaleDateString()} · {s.source}
                  </div>
                </div>
                <button
                  onClick={() => removeSub(s)}
                  data-testid={`subscriber-remove-${s.id}`}
                  className="p-2 text-[var(--trio-muted)] hover:text-red-500"
                  aria-label={`Remove ${s.email}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* --- Confirm blast dialog --- */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent
          data-testid="blast-confirm-dialog"
          className="bg-[var(--trio-bg)] border-[var(--trio-border)] rounded-none max-w-lg"
        >
          <DialogHeader>
            <DialogTitle className="font-display uppercase text-2xl tracking-tight flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-[var(--trio-primary)]" />
              Send to {activeCount} people?
            </DialogTitle>
            <DialogDescription className="text-sm text-[var(--trio-muted)]">
              This can't be undone. Double-check the subject and body below.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 border border-[var(--trio-border)] p-4 max-h-64 overflow-auto">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--trio-primary)] font-bold">
                Subject
              </div>
              <div className="text-sm font-medium">{subject}</div>
            </div>
            {preheader && (
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--trio-primary)] font-bold">
                  Preheader
                </div>
                <div className="text-xs text-[var(--trio-muted)]">{preheader}</div>
              </div>
            )}
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--trio-primary)] font-bold">
                Body
              </div>
              <div className="text-sm whitespace-pre-wrap">{body}</div>
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={() => setConfirmOpen(false)}
              disabled={blasting}
              data-testid="blast-cancel"
              className="px-5 py-2 text-xs tracking-widest uppercase border border-[var(--trio-border)] hover:border-[var(--trio-text)]"
            >
              Back
            </button>
            <button
              onClick={sendBlast}
              disabled={blasting}
              data-testid="blast-confirm-send"
              className="inline-flex items-center gap-2 bg-[var(--trio-primary)] hover:bg-[var(--trio-primary-hover)] text-white px-6 py-2 text-xs tracking-widest uppercase font-bold disabled:opacity-60"
            >
              {blasting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {blasting ? "Sending…" : `Send to ${activeCount}`}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
